import React, { useEffect, useRef, useState } from 'react';

const L1 = "We don't promise fast results.";
const L2 = 'We build habits that last for life.';
const SUB = 'No crash diets. No shortcuts. Just consistency—with guidance.';
const H1 = 'No pressure.';
const H2 = 'Just progress.';

const BASE_CHAR_MS = 38;
const PAUSE_MS = 420;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function HabitsTypewriterSection() {
  const rootRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [subtext, setSubtext] = useState('');
  const [hi1, setHi1] = useState('');
  const [hi2, setHi2] = useState('');
  const [caretFor, setCaretFor] = useState('');

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.22, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;

    let cancelled = false;

    const reduced =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setLine1(L1);
      setLine2(L2);
      setSubtext(SUB);
      setHi1(H1);
      setHi2(H2);
      setCaretFor('');
      return;
    }

    async function typeLine(text, setter, speed = BASE_CHAR_MS) {
      for (let i = 0; i <= text.length; i++) {
        if (cancelled) return;
        setter(text.slice(0, i));
        if (i < text.length) {
          const jitter = Math.random() * 22;
          await wait(speed + jitter);
        }
      }
    }

    async function run() {
      setCaretFor('l1');
      await typeLine(L1, setLine1);
      await wait(PAUSE_MS);
      setCaretFor('l2');
      await typeLine(L2, setLine2);
      await wait(PAUSE_MS * 1.15);
      setCaretFor('sub');
      await typeLine(SUB, setSubtext, 26);
      await wait(PAUSE_MS * 0.85);
      setCaretFor('h1');
      await typeLine(H1, setHi1, 44);
      await wait(PAUSE_MS * 0.65);
      setCaretFor('h2');
      await typeLine(H2, setHi2, 48);
      if (!cancelled) setCaretFor('');
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [started]);

  return (
    <section
      ref={rootRef}
      className="section habits-typewriter"
      id="habits-typewriter"
      aria-labelledby="habits-typewriter-heading"
    >
      <div className="habits-typewriter__bg" aria-hidden />
      <div className="habits-typewriter__ambient" aria-hidden>
        <div className="habits-typewriter__blob habits-typewriter__blob--a" />
        <div className="habits-typewriter__blob habits-typewriter__blob--b" />
      </div>

      <div className="habits-typewriter__shell">
        <h2 id="habits-typewriter-heading" className="screen-reader-label">
          We focus on lasting habits, not fast results — {SUB} {H1} {H2}
        </h2>

        <div className="habits-typewriter__layout">
          <div className="habits-typewriter__aside">
            <p className="habits-typewriter__eyebrow">
              <span className="habits-typewriter__eyebrow-dot" aria-hidden />
              What we believe
            </p>
            <div className="habits-typewriter__rail" aria-hidden />
          </div>

          <div className="habits-typewriter__panel">
            <div className="habits-typewriter__chrome" aria-hidden>
              <span className="habits-typewriter__chrome-pill">Live message</span>
              <span className="habits-typewriter__chrome-dots">
                <span className="habits-typewriter__chrome-dot habits-typewriter__chrome-dot--r" />
                <span className="habits-typewriter__chrome-dot habits-typewriter__chrome-dot--y" />
                <span className="habits-typewriter__chrome-dot habits-typewriter__chrome-dot--g" />
              </span>
            </div>

            <div className="habits-typewriter__board" aria-hidden="true">
              <p className="habits-typewriter__main">
                <span className="habits-typewriter__line">{line1}</span>
                {caretFor === 'l1' ? <span className="habits-typewriter__caret" /> : null}
              </p>
              <p className="habits-typewriter__main">
                <span className="habits-typewriter__line">{line2}</span>
                {caretFor === 'l2' ? <span className="habits-typewriter__caret" /> : null}
              </p>

              <p className="habits-typewriter__sub">
                <span>{subtext}</span>
                {caretFor === 'sub' ? <span className="habits-typewriter__caret habits-typewriter__caret--sub" /> : null}
              </p>

              <div className="habits-typewriter__highlight">
                <p className="habits-typewriter__highlight-line">
                  <span>{hi1}</span>
                  {caretFor === 'h1' ? <span className="habits-typewriter__caret habits-typewriter__caret--hi" /> : null}
                </p>
                <p className="habits-typewriter__highlight-line habits-typewriter__highlight-line--accent">
                  <span>{hi2}</span>
                  {caretFor === 'h2' ? <span className="habits-typewriter__caret habits-typewriter__caret--hi" /> : null}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
